/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import DS from 'ember-data';
import ClipboardJS from 'clipboard';
import WebhookModel from 'cio/models/webhook';
import { task } from 'ember-concurrency';
import AlertsService from 'cio/services/alerts';
import AuthService from 'cio/services/auth';
import AnalyticsService from 'cio/services/analytics';

enum ModalState {
  Closed = 'closed',
  Disable = 'disable',
  Delete = 'delete',
}

interface ReportingWebhooksListRowArgs {
  webhook: WebhookModel;
}

export default class ReportingWebhooksListRow extends Component<ReportingWebhooksListRowArgs> {
  @service router!: RouterService;
  @service alerts!: AlertsService;
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  @tracked modal: ModalState = ModalState.Closed;
  @tracked clipboard: ClipboardJS;

  readonly dropdownActions = {
    disable: {
      label: 'Disable',
      value: 'disable',
    },
    enable: {
      label: 'Enable',
      value: 'enable',
    },
    edit: {
      label: 'Edit',
      value: 'edit',
    },
    copyKey: {
      label: 'Copy signing key',
      value: 'copy_signing_key',
    },
    delete: {
      label: 'Delete',
      value: 'delete',
    },
  };

  constructor(owner: unknown, args: ReportingWebhooksListRowArgs) {
    super(owner, args);

    this.clipboard = new ClipboardJS(`[data-copy-target="${args.webhook.id}"]`, {
      text: () => this.args.webhook.signingKey,
    });

    this.clipboard.on('success', () => {
      this.alerts.success(
        'integrations.reporting-webhooks.copy-key.success',
        "We've copied the webhook signing key to your clipboard.",
      );

      this.analytics.track('integration', 'reporting_webhook_key_copied', this.analyticsMeta);
    });
  }

  willDestroy() {
    super.willDestroy();

    this.clipboard.destroy();
  }

  get isModalButtonDisabled() {
    return (
      this.deleteWebhook.isRunning ||
      this.disableWebhook.isRunning ||
      this.enableWebhook.isRunning ||
      !this.auth.hasPermission('journeys/reporting_webhooks/edit')
    );
  }

  get analyticsMeta() {
    return {
      name: 'Reporting Webhooks',
      category: 'Data management',
      endpointId: this.args.webhook.id,
      source: 'Webhook List Page',
    };
  }

  @action
  openDisableModal() {
    this.modal = ModalState.Disable;

    let allEnabledWebhooks = this.store.peekAll('webhook').filterBy('enabled', true);

    this.analytics.track('integration', 'disable_started', {
      ...this.analyticsMeta,
      disabledOrDeleted: allEnabledWebhooks.length === 1 ? 'Disabled' : undefined,
      endpointDisabledOrDeleted: 'Disabled',
    });
  }

  @action
  openDeleteModal() {
    this.modal = ModalState.Delete;

    let allWebhooks = this.store.peekAll('webhook');

    this.analytics.track('integration', 'disable_started', {
      ...this.analyticsMeta,
      disabledOrDeleted: allWebhooks.length === 1 ? 'Deleted' : undefined,
      endpointDisabledOrDeleted: 'Deleted',
    });
  }

  @action
  closeModal() {
    this.modal = ModalState.Closed;
  }

  @action
  onEdit(source: string, transition: boolean) {
    this.analytics.track('integration', 'edit_started', {
      ...this.analyticsMeta,
      source,
    });

    if (transition) {
      this.router.transitionTo('integrations.reporting-webhooks.edit', this.args.webhook.id);
    }
  }

  disableWebhook = task({ drop: true }, async () => {
    try {
      this.args.webhook.set('enabled', false);
      await this.args.webhook.save();

      this.alerts.success(
        'integrations.reporting-webhooks.disable.success',
        'Your webhook endpoint has been disabled.',
      );

      this.closeModal();
    } catch {
      this.alerts.error(
        'integrations.reporting-webhooks.disable.error',
        'We ran into a problem trying to disable your webhook endpoint. Please try again.',
        'Uh oh',
      );
    }
  });

  deleteWebhook = task({ drop: true }, async () => {
    try {
      await this.args.webhook.destroyRecord();

      this.alerts.success('integrations.reporting-webhooks.delete.success', 'Your webhook endpoint has been deleted.');
    } catch (err) {
      this.alerts.error(
        'integrations.reporting-webhooks.delete.error',
        'We ran into a problem trying to delete your webhook endpoint. Please try again.',
        'Uh oh',
      );
    }
  });

  enableWebhook = task({ drop: true }, async () => {
    if (this.args.webhook.isForceDisabled) {
      return;
    }

    try {
      this.args.webhook.enabled = true;
      this.analytics.track('integration', 'enable_started', {
        ...this.analyticsMeta,
        enabledOrReenabled: 'Re-Enabled',
      });
      await this.args.webhook.save();
    } catch {
      this.alerts.error(
        'integrations.reporting-webhooks.enable.error',
        'We ran into a problem trying to enable your webhook endpoint. Please try again.',
        'Uh oh',
      );
    }
  });
}
